'use client';

// imports
import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';

// lib files
import ReactFetchAbortError from '@/lib/errors/ReactFetchAbortError';
import { SearchFiltersObject } from '@/lib/content-services/search/search';
import { searchFiltersAtom } from '@/lib/atoms/search-filters';
import { searchResultsAtom, searchResultsStatus } from '@/lib/atoms/search-results';
import usePrevious from '@/lib/hooks/usePrevious';
import search from '@/lib/content-services/search/search';
import { objectsMatchShallow } from '@/lib/helpers/equality';
import { isEmpty } from '@/lib/helpers/empty';
import { getStationId } from '@/lib/profile';
import { SearchResults } from '@/lib/types/search/results';
import CompanionState from '@/lib/types/atoms/companionState';
function SearchRequest() {
  const [searchFilters] = useAtom(searchFiltersAtom);
  const previousFilters = usePrevious(searchFilters);
  const [_, setSearchResults] = useAtom(searchResultsAtom);
  const [resultsStatus, setResultsStatus] = useAtom(searchResultsStatus);
  const handleFilterUpdates = useCallback(async (filters: SearchFiltersObject, abortController: AbortController) => {
    if (resultsStatus === CompanionState.IsLoading) return;
    setResultsStatus(CompanionState.IsLoading);
    if (!isEmpty(filters.query)) {
      const stationId = getStationId();
      let results: SearchResults | null = null;
      try {
        if (stationId) {
          results = await search({
            ...filters,
            stationId
          }, {
            abortController
          });
        } else {
          results = await search(filters, {
            abortController
          });
        }
        if (results) {
          setSearchResults(results);
          setResultsStatus(CompanionState.IsLoadedWithData);
        } else {
          setResultsStatus(CompanionState.IsLoadedWithoutData);
        }
      } catch (error) {
        setResultsStatus(CompanionState.Error);
      }
    }
  }, [setSearchResults, resultsStatus, setResultsStatus]);
  useEffect(() => {
    if (previousFilters && !objectsMatchShallow(searchFilters, previousFilters)) {
      const abortController = new AbortController();
      handleFilterUpdates(searchFilters, abortController);
      return () => {
        abortController.abort(new ReactFetchAbortError('Call was superceeded or component unmounted'));
      };
    }

    /*
     * We are intentially ommitting handleFilterUpdates here to prevent unnecessary rerenders
     * - Adding handleFilterUpdates to the dependency array causes the useEffect to re-run unnecessarily,
     *   which leads to the premature firing of AbortController, in turn canceling in-flight requests before they complete.
     * - Currently, handleFilterUpdates only runs when searchFilters change, which prevents redundant network calls.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);
  return <></>;
}
export default SearchRequest;